import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
  type MenuCheckedValueChangeData,
} from "@fluentui/react-components";
import { ChevronLeft16Regular, ChevronRight16Regular, Dismiss16Regular, Filter16Regular } from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { FilterLabel, ListToolbar, menuBackLabel, useListToolbarStyles } from "@/components/list-toolbar";
import { usePageAction } from "@/shell/page-action";
import { FuiButton, type FuiOption } from "@/components/fui-control";

const useStyles = makeStyles({
  navGroup: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden",
    height: "28px",
  },
  navBtn: {
    border: "none",
    background: "transparent",
    height: "100%",
    padding: "0 8px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.15s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground2Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  navDivider: {
    width: "1px",
    height: "14px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  popover: {
    maxHeight: "70vh",
    overflowY: "auto",
    minWidth: "220px",
  },
});

export interface CalendarHeaderProps {
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  hideDone: boolean;
  onHideDoneChange: (v: boolean) => void;
  assignee: string;
  onAssigneeChange: (v: string) => void;
  assigneesList: FuiOption[];
}

export function CalendarHeader({
  onPrev,
  onNext,
  onToday,
  hideDone,
  onHideDoneChange,
  assignee,
  onAssigneeChange,
  assigneesList,
}: CalendarHeaderProps) {
  const styles = useStyles();
  usePageAction(null);

  return (
    <>
      <ListToolbar
        title="캘린더"
        filters={
          <CalendarFilter
            hideDone={hideDone}
            onHideDoneChange={onHideDoneChange}
            assignee={assignee}
            onAssigneeChange={onAssigneeChange}
            assigneesList={assigneesList}
          />
        }
        trail={
          <div className={styles.navGroup}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={onPrev}
              aria-label="이전"
              title="이전"
            >
              <ChevronLeft16Regular style={{ fontSize: 13 }} />
            </button>
            <div className={styles.navDivider} />
            <button
              type="button"
              className={styles.navBtn}
              onClick={onToday}
              aria-label="오늘"
              title="오늘"
              style={{ fontWeight: tokens.fontWeightRegular, padding: "0 12px" }}
            >
              오늘
            </button>
            <div className={styles.navDivider} />
            <button
              type="button"
              className={styles.navBtn}
              onClick={onNext}
              aria-label="다음"
              title="다음"
            >
              <ChevronRight16Regular style={{ fontSize: 13 }} />
            </button>
          </div>
        }
      />
    </>
  );
}

type CalendarFilterSection = "done" | "assignee";

function CalendarFilter({
  hideDone,
  onHideDoneChange,
  assignee,
  onAssigneeChange,
  assigneesList,
}: Pick<CalendarHeaderProps, "hideDone" | "onHideDoneChange" | "assignee" | "onAssigneeChange" | "assigneesList">) {
  const styles = useStyles();
  const toolbar = useListToolbarStyles();
  const [section, setSection] = useState<CalendarFilterSection | null>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const roots = useRef(new Map<CalendarFilterSection, HTMLDivElement>());
  const returnFocus = useRef<CalendarFilterSection | null>(null);
  const done = hideDone ? "hide" : "show";
  const people = assigneesList.map((option) => (option.value === "" ? { ...option, label: "전체" } : option));
  const count = (hideDone ? 1 : 0) + (assignee ? 1 : 0);
  const assigneeLabel = people.find((option) => option.value === assignee)?.label;
  const openSection = (next: CalendarFilterSection) => setSection(next);
  const goBack = () => {
    returnFocus.current = section;
    setSection(null);
  };
  useEffect(() => {
    if (section) {
      backRef.current?.focus();
      return;
    }
    const from = returnFocus.current;
    if (!from) return;
    returnFocus.current = null;
    roots.current.get(from)?.focus();
  }, [section]);
  const onChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    const next = data.checkedItems[0];
    if (next == null) return;
    if (data.name === "done" && next !== done) onHideDoneChange(next === "hide");
    if (data.name === "assignee" && next !== assignee) onAssigneeChange(next);
  };
  const row = (id: CalendarFilterSection, label: string, hint?: string) => (
    <MenuItem
      key={id}
      persistOnClick
      ref={(node) => {
        if (node) roots.current.set(id, node);
        else roots.current.delete(id);
      }}
      aria-label={hint ? `${label}, ${hint}` : label}
      secondaryContent={hint ? <span className={toolbar.menuHint} title={hint}>{hint}</span> : undefined}
      submenuIndicator={<ChevronRight16Regular />}
      onClick={() => openSection(id)}
      onKeyDown={(event) => {
        if (event.key !== "ArrowRight") return;
        event.preventDefault();
        event.stopPropagation();
        openSection(id);
      }}
    >
      {label}
    </MenuItem>
  );

  return (
    <Menu
      checkedValues={{ done: [done], assignee: [assignee] }}
      onCheckedValueChange={onChecked}
      onOpenChange={(_, data) => {
        if (!data.open) setSection(null);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <FuiButton className={toolbar.nowrap} icon={<Filter16Regular />} appearance="subtle" aria-label={count > 0 ? `필터: ${count}` : "필터"}>
          <FilterLabel count={count} />
        </FuiButton>
      </MenuTrigger>
      <MenuPopover
        className={styles.popover}
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" || !section) return;
          event.preventDefault();
          event.stopPropagation();
          goBack();
        }}
      >
        <MenuList aria-label="필터">
          {section == null ? (
            <>
              {row("done", "완료", hideDone ? "숨김" : undefined)}
              {row("assignee", "담당", assignee ? assigneeLabel : undefined)}
              {count > 0 ? (
                <>
                  <MenuDivider />
                  <MenuItem
                    icon={<Dismiss16Regular />}
                    onClick={() => {
                      onHideDoneChange(false);
                      onAssigneeChange("");
                    }}
                  >
                    필터 초기화
                  </MenuItem>
                </>
              ) : null}
            </>
          ) : (
            <>
              <MenuItem ref={backRef} persistOnClick icon={<ChevronLeft16Regular />} aria-label={menuBackLabel(section === "done" ? "완료" : "담당")} onClick={goBack}>
                {section === "done" ? "완료" : "담당"}
              </MenuItem>
              <MenuDivider />
              {section === "done" ? (
                <>
                  <MenuItemRadio name="done" value="show">표시</MenuItemRadio>
                  <MenuItemRadio name="done" value="hide">숨김</MenuItemRadio>
                </>
              ) : (
                people.map((option) => (
                  <MenuItemRadio key={option.value || "all"} name="assignee" value={option.value}>
                    {option.label}
                  </MenuItemRadio>
                ))
              )}
            </>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
